import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    OnDestroy,
    OnInit,
    computed,
    effect,
    inject,
    input,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { IonButton, IonIcon, IonItem, IonLabel } from '@ionic/angular/standalone';
import { ResizableModule, ResizeEvent } from 'angular-resizable-element';
import { BehaviorSubject, Subscription, combineLatest, delayWhen, distinctUntilChanged, filter, map, of } from 'rxjs';
import { SignalChartComponent } from './signal-chart.component';
import { delayToFrame } from '../../hgm-setup/src/module/util/core';
import { LOGGER } from '../../hgm-setup/src/module/util/logger';
import { UiOptions } from '../../hgm-setup/src/module/util/ui-options';
import { DataProvider, SignalNoData } from '../../providers/data-provider';
import { LogFileOpenRequester } from '../../providers/log-file-open-requester';
import { GroupSignal, SignalGroupProvider } from '../../providers/signal-group-provider';
import { RangeMap, SignalSettingsProvider, StyleMap } from '../../providers/signal-settings-provider';
import { XAxisManager, XLimits } from '../../providers/x-axis-manager';

class StyledGroup<T extends GroupSignal | SignalNoData = GroupSignal | SignalNoData> {
    style: { [key: string]: any };
    isUpdating = true;

    private heightScaleInt = 1;
    private nominalHeightInt: number;

    constructor(
        public group: T[],
        public styles: StyleMap,
        public ranges: RangeMap,
        nominalHeight: number,
    ) {
        this.nominalHeightInt = nominalHeight;
        this.makeStyle();
    }

    get heightScale() {
        return this.heightScaleInt;
    }

    set heightScale(value) {
        this.heightScaleInt = value;
        this.makeStyle();
    }

    get nominalHeight() {
        return this.nominalHeightInt;
    }

    set nominalHeight(value) {
        this.nominalHeightInt = value;
        this.makeStyle();
    }

    get height() {
        return this.nominalHeight * this.heightScale;
    }

    set height(value) {
        this.heightScaleInt = value / this.nominalHeight;
        this.makeStyle();
    }

    private makeStyle() {
        const height = Math.floor(this.nominalHeight * this.heightScale);
        this.style = {
            height: `${height}px`,
            'max-height': `${height}px`,
        };
    }
}

const minNominalHeight = 240;

@Component({
    selector: 'app-tab-charts',
    templateUrl: 'tab-charts.page.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IonButton, IonIcon, IonItem, IonLabel, SignalChartComponent, ResizableModule],
})
export class TabChartsPageComponent implements OnInit, OnDestroy {
    readonly uiOptions = inject(UiOptions);
    styledGroups: StyledGroup<GroupSignal>[] = [];
    styledGroupsNoData: StyledGroup<SignalNoData>[] = [];

    tabHeight = input(0);
    viewfinderIsUpdating = input(true);
    readonly dataError = computed(() => this.dataProvider.result()?.error);
    readonly shortcutPrefix = /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘' : 'Ctrl+';
    readonly isLoaded = computed(() => !!this.groupProvider.styledGroups());

    private subscriptions = new Array<Subscription>();
    private isUpdatingSubj = new BehaviorSubject(true);
    private signalsAreUpdatingSubj = new BehaviorSubject(true);
    private isViewActive = false;
    private deferredKey: KeyboardEvent | undefined;
    private viewfinderIsUpdatingObs = toObservable(this.viewfinderIsUpdating);

    private readonly dataProvider = inject(DataProvider);
    private readonly groupProvider = inject(SignalGroupProvider);
    private readonly signalSetter = inject(SignalSettingsProvider);
    private readonly xAxisManager = inject(XAxisManager);
    private readonly logger = inject(LOGGER);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly openRequester = inject(LogFileOpenRequester);

    constructor() {
        effect(() => {
            const th = this.tabHeight();
            const nominalHeight = this.nominalHeight(this.styledGroups.length);
            this.styledGroups.forEach((styledGroup) => (styledGroup.nominalHeight = nominalHeight));
        });
        const rangesSignal = toSignal(this.signalSetter.rangesObs);
        effect(() => {
            const data = this.groupProvider.styledGroupsNoData() ?? { groups: [], styles: {} };
            const ranges = rangesSignal();
            if (!ranges) return;
            const { groups, styles } = data;
            this.styledGroupsNoData = groups.map((group) => new StyledGroup(group, styles, ranges, this.nominalHeight(groups.length)));
            this.cdr.markForCheck();
        });
        effect(() => {
            const data = this.groupProvider.styledGroups() ?? { groups: [], styles: {} };
            const ranges = rangesSignal();
            if (!ranges) return;
            const { groups, styles } = data;
            this.xAxisManager.cursorPos = 0;
            this.deferredKey = undefined;
            this.styledGroups = groups.map((group) => new StyledGroup(group, styles, ranges, this.nominalHeight(groups.length)));
            this.cdr.markForCheck();
        });
    }

    ngOnInit() {
        const isUpdatingObs = combineLatest([this.signalsAreUpdatingSubj, this.viewfinderIsUpdatingObs]).pipe(
            map(([signals, charts]) => signals || charts),
        );
        this.subscriptions.push(
            isUpdatingObs.pipe(delayWhen((value) => (value ? of(null) : delayToFrame()))).subscribe(this.isUpdatingSubj),
        );
        this.subscriptions.push(
            this.isUpdatingSubj
                .pipe(
                    distinctUntilChanged(),
                    filter((v) => !v),
                )
                .subscribe(() => {
                    if (this.deferredKey) {
                        const deferredKey = this.deferredKey;
                        this.deferredKey = undefined;
                        this.handleKey(deferredKey);
                    }
                }),
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions = [];
    }

    ionViewDidEnter() {
        this.isViewActive = true;
    }

    ionViewDidLeave() {
        this.isViewActive = false;
        this.deferredKey = undefined;
    }

    openLogFile() {
        this.openRequester.requestOpen();
    }

    onChartResizing(event: ResizeEvent, styledGroup: StyledGroup) {
        if (event.rectangle.height === undefined) {
            this.logger.error(`Chart resize event's rectangle had no height`);
            return;
        }
        styledGroup.height = event.rectangle.height;
    }

    onChartResizeEnd(event: ResizeEvent, styledGroup: StyledGroup) {
        this.onChartResizing(event, styledGroup);
    }

    onChartIsUpdating(isUpdating: boolean, styledGroup: StyledGroup) {
        styledGroup.isUpdating = isUpdating;
        this.signalsAreUpdatingSubj.next(this.styledGroups.some((group) => group.isUpdating));
    }

    @HostListener('window:keydown', ['$event'])
    onKeydown(event: KeyboardEvent) {
        if (!this.isViewActive) {
            return;
        }

        // Skip hotkeys if the user is typing in an input field (like the formula builder modal)
        const target = event.target as HTMLElement;
        const tag = target?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || target?.isContentEditable) {
            return;
        }

        if (this.isUpdatingSubj.value) {
            // if we already have a deferred key, discard this one
            this.deferredKey = this.deferredKey || event;
        }
        if (!this.deferredKey) {
            this.handleKey(event);
        }
    }

    private handleKey(event: KeyboardEvent) {
        const span = this.xAxisManager.userFineLimits[1] - this.xAxisManager.userFineLimits[0];
        const step = event.shiftKey ? span : event.ctrlKey ? span / 50 : span / 10;
        let delta = 0;
        switch (event.key) {
            case 'ArrowRight':
            case ' ':
                event.preventDefault(); // Stop spacebar scrolling
                delta = Math.min(step, this.xAxisManager.userCoarseLimits[1] - this.xAxisManager.userFineLimits[1]);
                break;
            case 'ArrowLeft':
                delta = Math.max(-step, this.xAxisManager.userCoarseLimits[0] - this.xAxisManager.userFineLimits[0]);
                break;
        }
        if (delta !== 0) {
            this.xAxisManager.userFineLimits = this.xAxisManager.userFineLimits.map((l) => l + delta) as XLimits;
        }
    }

    private nominalHeight(nGroups: number) {
        const nCharts = Math.max(nGroups, 1);
        return Math.max(this.tabHeight() / nCharts, minNominalHeight);
    }
}
